<template>
  <div class="day-wrap">
    <div class="day">
      <date-picker
        v-model="selectedDate"
        :format="'DD/MM/YYYY'"
        placeholder="DD/MM/YYYY"
        class="custom-date-picker"
      />
    </div>

    <div class="hours-select-wrap">
      <div class="hours-range">
        <label class="checkbox-wrap open-all-day">
          <input
            v-model="isOpenAllDay"
            type="checkbox"
            class="enable-checkbox"
            :title="$t('INBOX_MGMT.BUSINESS_HOURS.ALL_DAY')"
          />
          <span>{{ $t('INBOX_MGMT.BUSINESS_HOURS.ALL_DAY') }}</span>
        </label>

        <multiselect
          v-model="fromTime"
          :options="fromTimeSlots"
          :disabled="isOpenAllDay || isClosedAllDay"
          :placeholder="$t('INBOX_MGMT.BUSINESS_HOURS.DAY.CHOOSE')"
          :allow-empty="false"
          :style="{ minWidth: '150px' }"
        />

        <div class="separator-icon">
          <fluent-icon icon="subtract" type="solid" size="16" />
        </div>

        <multiselect
          v-model="toTime"
          :options="toTimeSlots"
          :disabled="isOpenAllDay || isClosedAllDay"
          :placeholder="$t('INBOX_MGMT.BUSINESS_HOURS.DAY.CHOOSE')"
          :allow-empty="false"
          :style="{ minWidth: '150px' }"
        />
      </div>

      <div v-if="hasError" class="date-error">
        <span class="error">
          {{ $t('INBOX_MGMT.BUSINESS_HOURS.DAY.VALIDATION_ERROR') }}
        </span>
      </div>
    </div>

    <div class="actions-wrap-right">
      <span v-if="!hasError" class="label">
        {{ totalHours }} {{ $t('INBOX_MGMT.BUSINESS_HOURS.DAY.HOURS') }}
      </span>

      <woot-button
        v-tooltip.top="$t('ATTRIBUTES_MGMT.DELETE.BUTTON_TEXT')"
        variant="smooth"
        size="small"
        color-scheme="alert"
        class="grey-btn btn-margin"
        icon="dismiss-circle"
        @click="deleteCustomSlot"
      />
    </div>
  </div>
</template>

<script>
import { differenceInMinutes, parse } from "date-fns";
import DatePicker from "vue2-datepicker";
import { generateTimeSlots } from "../helpers/businessHour";

const timeSlots = generateTimeSlots(30);

export default {
  components: { DatePicker },
  props: {
    timeSlot: {
      type: Object,
      required: true,
    },
  },
  data() {
    if (this.timeSlot.day && this.timeSlot.month && this.timeSlot.year) {
      return {
        selectedDate: new Date(this.timeSlot.year, this.timeSlot.month - 1, this.timeSlot.day)
      }
    }
    return {
      selectedDate: this.timeSlot.date ? new Date(this.timeSlot.date) : null,
    };
  },
  watch: {
    selectedDate(newDate) {
      if (newDate) {
        this.$emit("update", {
          ...this.timeSlot,
          day: newDate.getDate(),
          month: newDate.getMonth() + 1,
          year: newDate.getFullYear(),
          date: newDate,
        });
      }
    },
  },
  computed: {
    fromTimeSlots() {
      return timeSlots;
    },
    toTimeSlots() {
      return timeSlots.filter((slot) => slot !== "12:00 AM");
    },
    fromTime: {
      get() {
        return this.timeSlot.from;
      },
      set(value) {
        this.updateTimeSlot("from", value);
      },
    },
    toTime: {
      get() {
        return this.timeSlot.to;
      },
      set(value) {
        this.updateTimeSlot("to", value);
      },
    },
    fromDate() {
      return parse(this.fromTime, "hh:mm a", new Date());
    },
    toDate() {
      return parse(this.toTime, "hh:mm a", new Date());
    },
    totalHours() {
      if (this.isClosedAllDay) return 0;
      const hours = this.isOpenAllDay ? 24 : differenceInMinutes(this.toDate, this.fromDate) / 60;
      return Math.round(hours * 10) / 10;
    },
    hasError() {
      return !this.timeSlot.valid;
    },
    isOpenAllDay: {
      get() {
        return this.timeSlot.openAllDay;
      },
      set(value) {
        this.timeSlot.openAllDay = value;
  this.$emit("update", {
    ...this.timeSlot,
    from: value ? "12:00 AM" : "09:00 AM",
    to: value ? "11:59 PM" : "05:00 PM",
    valid: true,
    open_all_day: value,
    closed_all_day: false,
  });
},
    },
    isClosedAllDay: {
      get() {
        return this.timeSlot.closed_all_day;
      },
      set(value) {
        this.$emit("update", {
          ...this.timeSlot,
          from: "",
          to: "",
          valid: true,
          closed_all_day: value,
          open_all_day: false,
        });
      },
    },
  },
  methods: {
    updateTimeSlot(key, value) {
      const newTime = parse(value, "hh:mm a", new Date());
      const valid =
        key === "from"
          ? differenceInMinutes(this.toDate, newTime) > 0
          : differenceInMinutes(newTime, this.fromDate) > 0;

      this.$emit("update", {
        ...this.timeSlot,
        [key]: value,
        valid,
        closed_all_day: false,
        open_all_day: false
      });
    },
    deleteCustomSlot(e) {
      e.preventDefault();
      e.stopPropagation();
      this.$emit("delete");
    },
  },
};
</script>

<style lang="scss" scoped>
.custom-date-picker {
  width: 14rem;
}

.day-wrap {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-small) 0;
  border-bottom: 1px solid var(--color-border-light);
}

.enable-checkbox {
  margin: 0;
}

.hours-select-wrap {
  display: flex;
  flex-direction: row;
  position: relative;
}

.hours-range {
  display: flex;
  align-items: center;
  flex-grow: 1;
  margin-left: var(--space-medium);
}

.checkbox-wrap {
  display: flex;
  align-items: center;
  margin-right: var(--space-medium);
  width: fit-content;
  text-wrap: nowrap;

  span {
    font-size: var(--font-size-small);
    margin-left: var(--space-smaller);
  }
}

.separator-icon {
  padding: 0 var(--space-slab);
}

.actions-wrap {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-left: var(--space-medium);
}

.label {
  font-size: var(--font-size-mini);
  color: var(--w-700);
  background: var(--w-50);
}

.actions-wrap-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-left: auto;
}

.date-error {
  padding-top: var(--space-smaller);
}

.error {
  font-size: var(--font-size-mini);
  color: var(--r-300);
}
</style>
